import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { obtenerPerfilUsuario, cambiarContraseña } from "../utils/acciones";
import { getHomeRouteByRole } from "../utils/rutas";
import { loadCoursesForPerfil } from "../utils/perfilCursosInscritosList";
import ProfileBillingSection from "../components/ProfileBillingSection";
import PasswordVisibilityField from "../components/PasswordVisibilityField";
import ViewportSelect from "../components/ViewportSelect";
import ResponsiveActionSidebar from "../components/ResponsiveActionSidebar";
import { API_URL } from "../config";
import "./Perfil_usuarioS.css";

const Perfil = () => {
  const navigate = useNavigate();
  const [mensaje, setMensaje] = useState("");

  // Ruta de inicio
  const [rol, setRol] = useState("");

  useEffect(() => {
    fetch(`${API_URL}/verificar-token`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        if (data.valid) setRol(data.usuario.rol);
      })
      .catch((err) => console.error("Error obteniendo rol:", err));
  }, []);


  // Estados del usuario
  const [usuario, setUsuario] = useState(null);
  const [cursosInscritos, setCursosInscritos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingPassword, setSavingPassword] = useState(false);

  // Estados para contraseñas
  const [contraseñaActual, setContraseñaActual] = useState("");
  const [nuevaContraseña, setNuevaContraseña] = useState("");
  const [confirmarNuevaContraseña, setConfirmarNuevaContraseña] = useState("");
  const [showAllPasswords, setShowAllPasswords] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Obtener datos del usuario al cargar la página
  useEffect(() => {
    const fetchPerfil = async () => {
      try {
        const data = await obtenerPerfilUsuario();
        if (data) {
          let role = typeof data.rol === "string" ? data.rol : "";
          if (!role) {
            try {
              const tokenResponse = await fetch(`${API_URL}/verificar-token`, { credentials: "include" });
              const tokenData = await tokenResponse.json();
              if (tokenData?.valid && typeof tokenData.usuario?.rol === "string") {
                role = tokenData.usuario.rol;
              }
            } catch {
              role = "";
            }
          }
          if (role) {
            setRol(role);
          }
          setUsuario({ ...data, rol: role || data.rol });
          setCursosInscritos(await loadCoursesForPerfil({ ...data, rol: role }));
        } else {
          setMensaje("No se pudo cargar el perfil.");
        }
      } catch (error) {
        console.error("Error al obtener perfil:", error);
        setMensaje("Error al cargar el perfil.");
      } finally {
        setLoading(false);
      }
    };
    fetchPerfil();
  }, []);

  const handleCambiarContraseña = async () => {
    setMensaje("");
    if (!contraseñaActual || !nuevaContraseña || !confirmarNuevaContraseña) {
      setMensaje("Introduce la contraseña actual, la nueva y la confirmación.");
      return;
    }
    if (nuevaContraseña !== confirmarNuevaContraseña) {
      setMensaje("Las nuevas contraseñas no coinciden.");
      return;
    }
    try {
      setSavingPassword(true);
      const respuesta = await cambiarContraseña(contraseñaActual, nuevaContraseña, confirmarNuevaContraseña);
      if (respuesta.error) {
        throw new Error(respuesta.error);
      }
      setMensaje("Contraseña actualizada correctamente.");
      setContraseñaActual("");
      setNuevaContraseña("");
      setConfirmarNuevaContraseña("");
    } catch (error) {
      console.error("Error al cambiar la contraseña:", error);
      setMensaje(error.message || "Error al cambiar la contraseña.");
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="perf-container student-loading-screen">
        <h2 className="perfil-titulo">Cargando perfil...</h2>
      </div>
    );
  }

  if (!usuario) {
    return <p>Error al cargar el perfil. Inténtalo de nuevo.</p>;
  }

  const formattedFecha = (() => {
    if (!usuario.fechaNacimiento) {
      return "";
    }
    const parsed = new Date(usuario.fechaNacimiento);
    if (Number.isNaN(parsed.getTime())) {
      return "";
    }
    return parsed.toISOString().split("T")[0];
  })();


  //Volver a pagina de inicio de cada usuario
   const handleVolver = () => {
  navigate(getHomeRouteByRole(rol), { replace: true });
};



  return (
    <div className="perf-container">
      <div className="perf-main-container">
        <div className="perf-content">
          <h1 className="perfil-titulo">Bienvenido a tu perfil</h1>
          {mensaje && <p className="perf-message">{mensaje}</p>}

          <div className="perf-form">
            <div className="form-group">
              <label>DNI:</label>
              <input type="text" className="form-control" value={usuario.dni ?? ""} disabled />
            </div>
            <div className="form-group">
              <label>Nombre:</label>
              <input type="text" className="form-control" value={usuario.nombre ?? ""} disabled />
            </div>
            <div className="form-group">
              <label>Primer Apellido:</label>
              <input type="text" className="form-control" value={usuario.primerApellido ?? ""} disabled />
            </div>
            <div className="form-group">
              <label>Segundo Apellido:</label>
              <input type="text" className="form-control" value={usuario.segundoApellido ?? ""} disabled />
            </div>
            <div className="form-group">
              <label>Fecha de Nacimiento:</label>
              <input
                type="date"
                className="form-control"
                value={formattedFecha}
                disabled
                readOnly
              />
            </div>
            <div className="form-group">
              <label>Teléfono:</label>
              <input
                type="text"
                className="form-control"
                value={usuario.telefono ?? ""}
                disabled
                readOnly
                maxLength="9"
              />
            </div>

            <ProfileBillingSection
              direccion={usuario.direccion || ""}
              ciudad={usuario.ciudad || ""}
              codigoPostal={usuario.codigoPostal || ""}
              country={usuario.country || usuario.pais || "ES"}
              onDireccionChange={() => {}}
              onCiudadChange={() => {}}
              onCodigoPostalChange={() => {}}
              onCountryChange={() => {}}
              readOnly
            />

            <div className="form-group">
              <label>Email:</label>
              <input type="email" className="form-control" value={usuario.email ?? ""} disabled />
            </div>
            <div className="form-group">
              <label>Cursos Inscritos:</label>
              <div
                className="cursos-container">
                {cursosInscritos.map((c) => (
                  <ViewportSelect
                    key={c.id}
                    className="form-control curso-field"
                    value={c.id}
                    disabled
                    aria-label={c.titulo || "Curso inscrito"}
                  >
                    <option value={c.id}>{c.titulo || "Curso sin título"}</option>
                  </ViewportSelect>
                ))}
                {cursosInscritos.length === 0 && (
                  <p className="perf-empty-courses">No estás inscrito en ningún curso.</p>
                )}
              </div>
            </div>
            <div className="form-group">
              <label>Contraseñas:</label>
              <div className="passwords-container perf-passwords-container">
                <PasswordVisibilityField
                  id="perfil-current-password"
                  wrapperClassName="password-visibility-field perf-password-field"
                  toggleClassName="password-visibility-toggle perf-password-toggle"
                  placeholder="Contraseña Actual"
                  value={contraseñaActual}
                  onChange={(e) => setContraseñaActual(e.target.value)}
                  visible={showAllPasswords || showCurrentPassword}
                  onToggleVisible={() => {
                    if (showAllPasswords) setShowAllPasswords(false);
                    else setShowCurrentPassword((visible) => !visible);
                  }}
                  showLabelKey="passwordVisibility.showCurrent"
                  hideLabelKey="passwordVisibility.hideCurrent"
                  maxLength={128}
                  autoComplete="current-password"
                />
                <PasswordVisibilityField
                  id="perfil-new-password"
                  wrapperClassName="password-visibility-field perf-password-field"
                  toggleClassName="password-visibility-toggle perf-password-toggle"
                  placeholder="Nueva Contraseña"
                  value={nuevaContraseña}
                  onChange={(e) => setNuevaContraseña(e.target.value)}
                  visible={showAllPasswords || showNewPassword}
                  onToggleVisible={() => {
                    if (showAllPasswords) setShowAllPasswords(false);
                    else setShowNewPassword((visible) => !visible);
                  }}
                  showLabelKey="resetPassword.showPassword"
                  hideLabelKey="resetPassword.hidePassword"
                  maxLength={128}
                  autoComplete="new-password"
                />
                <PasswordVisibilityField
                  id="perfil-confirm-password"
                  wrapperClassName="password-visibility-field perf-password-field"
                  toggleClassName="password-visibility-toggle perf-password-toggle"
                  placeholder="Confirmar Contraseña"
                  value={confirmarNuevaContraseña}
                  onChange={(e) => setConfirmarNuevaContraseña(e.target.value)}
                  visible={showAllPasswords || showConfirmPassword}
                  onToggleVisible={() => {
                    if (showAllPasswords) setShowAllPasswords(false);
                    else setShowConfirmPassword((visible) => !visible);
                  }}
                  showLabelKey="resetPassword.showConfirm"
                  hideLabelKey="resetPassword.hideConfirm"
                  maxLength={128}
                  autoComplete="new-password"
                />
              </div>
            </div>
          </div>
        </div>

        <ResponsiveActionSidebar
          id="perfil-sidebar"
          className="perf-sidebar"
          openClassName="perf-sidebar--open"
          ariaLabel="Acciones del perfil"
        >
          <h4>Acciones</h4>
          <ul className="perf-sidebar-links">
            <li>
              <button className="perf-sidebar-button" onClick={handleCambiarContraseña} disabled={savingPassword}>
                {savingPassword ? "Cambiando contraseña…" : "Cambiar contraseña"}
              </button>
            </li>
            <li>
              <button className="perf-sidebar-button" onClick={() => setShowAllPasswords(!showAllPasswords)}>
                {showAllPasswords ? "Ocultar Contraseñas" : "Mostrar Contraseñas"}
              </button>
            </li>
            <li>
            <button className="perf-sidebar-button staff-sidebar-exit-button staff-sidebar-exit-button--discard" onClick={handleVolver}>
              Cancelar y salir
            </button>
            </li>
          </ul>
        </ResponsiveActionSidebar>
      </div>
    </div>
  );
};

export default Perfil;
